import {
    INDUSTRIES
} from '@/config/constants';
import { useTranslation } from '@client-app/i18n';
import {
    getMemberAvatarUrl,
    Permission, useAuth
} from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { agentService } from '@common/api/agentService';
import { featureService } from '@common/api/featureService';
import {
    CaretDown,
    Check,
    CircleNotch,
    MagnifyingGlass,
    Upload
} from '@phosphor-icons/react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import {
    useEffect, useRef, useState
} from 'react';
import {
    isSoloInviteUpgradeRequired,
    resolveInvitePlanCode,
} from './memberInviteAccess';
import Modal from './Modal';
import { formatRoleLabel } from './roleLabels';

// WorkspaceAvatar component - shows logo or fallback to initial
interface WorkspaceAvatarProps {
    logoUrl?: string;
    name: string;
    className?: string;
    fontSize?: string;
}

const WorkspaceAvatar = ({
    logoUrl,
    name,
    className,
    fontSize = 'text-sm'
}: WorkspaceAvatarProps) => {
    const [imageError, setImageError] = useState(false);

    if (logoUrl && !imageError) {
        return (
            <img
                src={logoUrl}
                alt={name}
                className={clsx('w-full h-full object-contain p-0.5', className)}
                onError={() => setImageError(true)}
            />
        );
    }

    return (
        <div
            className={clsx(
                'flex h-full w-full items-center justify-center bg-[#F4F5F8] font-medium text-[#1A162F]',
                fontSize,
                className
            )}
        >
            {name?.charAt(0).toUpperCase() || 'W'}
        </div>
    );
};

type TransferCandidate = {
    userId: string;
    displayName: string;
    email: string;
    role: string;
};

const STALE_OWNER_TRANSFER_ERROR_PATTERNS = [
    /pending ownership transfer request.*not found/i,
    /ownership transfer request.*expired/i,
    /ownership transfer request.*no longer pending/i,
];

const isStaleOwnerTransferError = (error: unknown) => {
    const message = error instanceof Error
        ? error.message
        : String((error as { message?: unknown } | null | undefined)?.message || '');

    return STALE_OWNER_TRANSFER_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

const WorkspaceSettings = () => {
    const { t } = useTranslation();
    const {
        currentWorkspace, refreshWorkspaces, permissions, workspaceMembers, userBilling
    } = useAuth();
    const { showToast } = useToast();
    const {
        members,
        pendingOwnerTransfer,
        transferOwnership,
        cancelOwnerTransfer,
        refreshPendingOwnerTransfer,
    } = workspaceMembers;
    const canEdit = permissions.checkPermissions([Permission.WORKSPACE_UPDATE]);
    const [workspaceName, setWorkspaceName] = useState(currentWorkspace?.name || '');
    const [logoUrl, setLogoUrl] = useState(currentWorkspace?.logo_url || '');
    const [industry, setIndustry] = useState('ecommerce');
    const [otherIndustry, setOtherIndustry] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isIndustryDropdownOpen, setIsIndustryDropdownOpen] = useState(false);
    const [selectedTransferUserId, setSelectedTransferUserId] = useState('');
    const [isTransferDropdownOpen, setIsTransferDropdownOpen] = useState(false);
    const [transferSearchQuery, setTransferSearchQuery] = useState('');
    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [isCancellingOwnerTransfer, setIsCancellingOwnerTransfer] = useState(false);
    const [transferTargetSnapshot, setTransferTargetSnapshot] = useState<TransferCandidate | null>(null);

    const logoInputRef = useRef<HTMLInputElement>(null);

    const workspacePlanCode = resolveInvitePlanCode({
        currentWorkspacePlanCode: currentWorkspace?.subscription?.plan_code,
        billingSummaryPlanCode: userBilling.summary?.planCode,
        userPlanCode: userBilling.userPlan?.planCode,
    });
    const isSoloWorkspace = isSoloInviteUpgradeRequired(workspacePlanCode);
    const canTransferOwnership = permissions.canTransferOwnership && !isSoloWorkspace;
    const canStartOwnerTransfer = canTransferOwnership && !pendingOwnerTransfer;

    const allMembers = members;
    const transferCandidates: TransferCandidate[] = allMembers
        .filter((member: any) => {
            const status = String(member.status || '').toUpperCase();
            const role = String(member.role || '').toUpperCase();
            return status === 'ACTIVE' && !member.isCurrentUser && role !== 'OWNER';
        })
        .map((member: any) => ({
            userId: String(member.userId || member.id),
            displayName: member.displayName || member.email?.split('@')[0] || 'User',
            email: member.email || '',
            role: String(member.dataRoleName || member.data_role_name || member.role || ''),
        }))
        .sort((a: TransferCandidate, b: TransferCandidate) => {
            const priority: Record<string, number> = { ADMIN: 0, MEMBER: 1 };
            const aPriority = priority[a.role.toUpperCase()] ?? 99;
            const bPriority = priority[b.role.toUpperCase()] ?? 99;
            if (aPriority !== bPriority) return aPriority - bPriority;
            return a.displayName.localeCompare(b.displayName);
        });

    const selectedTransferCandidate = transferCandidates.find(m => m.userId === selectedTransferUserId) || null;
    const selectedTransferTarget = selectedTransferCandidate
        || (isTransferring && transferTargetSnapshot?.userId === selectedTransferUserId ? transferTargetSnapshot : null);

    const filteredTransferCandidates = transferCandidates.filter(member =>
        member.displayName.toLowerCase().includes(transferSearchQuery.toLowerCase())
        || member.email.toLowerCase().includes(transferSearchQuery.toLowerCase())
    );

    // Load initial data
    useEffect(() => {
        if (currentWorkspace) {
            setWorkspaceName(currentWorkspace.name);
            setLogoUrl(currentWorkspace.logo_url || '');
            const currentIndustry = currentWorkspace.settings?.industry || 'ecommerce';
            // 检查是否在预定义列表中，不在则视为 other
            const industryExists = INDUSTRIES.find(i => i.id === currentIndustry);
            if (industryExists) {
                setIndustry(currentIndustry);
                setOtherIndustry('');
            } else {
                setIndustry('other');
                setOtherIndustry(currentIndustry);
            }
        }
    }, [currentWorkspace]);

    // Handle logo upload
    const handleLogoUpload = async (file: File) => {
        if (!file) return;

        setIsUploading(true);
        try {
            // Use agentService to upload file
            const response = await agentService.uploadFile(file);

            if (response.success && response.file_url) {
                setLogoUrl(response.file_url);
            } else {
                throw new Error('Upload failed');
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
        } finally {
            setIsUploading(false);
        }
    };

    // Handle save
    const handleSave = async () => {
        if (!currentWorkspace?.id) return;

        setIsSaving(true);
        try {
            await featureService.updateWorkspace(currentWorkspace.id, {
                name: workspaceName,
                logoUrl,
                settings: {
                    ...(currentWorkspace.settings || {}),
                    // 如果选择 other，保存用户输入的值，否则保存选中的 id
                    industry: industry === 'other' ? otherIndustry : industry,
                },
            });

            // Refresh workspaces to sync data in useWorkspaces hook
            await refreshWorkspaces({ silent: true });
        } catch (error) {
            console.error('Error saving workspace:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleTransferOwnership = async () => {
        if (!selectedTransferTarget) return;

        setIsTransferring(true);
        try {
            await transferOwnership(selectedTransferTarget.userId, 'TRANSFER');

            setIsTransferModalOpen(false);
            setSelectedTransferUserId('');
            setTransferTargetSnapshot(null);
            setTransferSearchQuery('');

            showToast(
                t('website.workspaceSettings.ownerTransferRequestSent', {
                    name: selectedTransferTarget.displayName,
                }),
                'success'
            );
        } catch (error: any) {
            showToast(
                t('website.workspaceSettings.transferFailed', {
                    reason: error.message || t('website.workspaceSettings.transferFailedFallback'),
                }),
                'error'
            );
        } finally {
            setIsTransferring(false);
        }
    };

    const cancelOwnershipTransfer = async () => {
        if (!pendingOwnerTransfer?.requestId) return;

        setIsCancellingOwnerTransfer(true);
        try {
            await cancelOwnerTransfer(pendingOwnerTransfer.requestId);
            showToast(t('website.workspaceSettings.ownershipTransferCancelled'), 'success');
        } catch (error: any) {
            if (isStaleOwnerTransferError(error)) {
                await refreshPendingOwnerTransfer();
                showToast(t('website.workspaceSettings.ownerTransferNoLongerPending'), 'info');
                return;
            }

            showToast(
                t('website.workspaceSettings.transferFailed', {
                    reason: error.message || t('website.workspaceSettings.transferFailedFallback'),
                }),
                'error'
            );
        } finally {
            setIsCancellingOwnerTransfer(false);
        }
    };

    const selectedIndustryData = INDUSTRIES.find((i) => i.id === industry);
    const getIndustryCopy = (industryOption: (typeof INDUSTRIES)[number]) => ({
        label: t(`website.workspaceSettings.industries.${industryOption.id}.label`),
        description: t(`website.workspaceSettings.industries.${industryOption.id}.description`),
    });
    const selectedIndustryCopy = selectedIndustryData ? getIndustryCopy(selectedIndustryData) : null;
    // 获取实际要保存的 industry 值
    const finalIndustry = industry === 'other' ? otherIndustry : industry;
    const hasChanges = workspaceName !== currentWorkspace?.name
        || logoUrl !== currentWorkspace?.logo_url
        || finalIndustry !== currentWorkspace?.settings?.industry;

    return (
        <div className="space-y-6">
            <div className="rounded-xl border border-[#E8EBF3] bg-[#FCFCFC] p-6 shadow-[0_12px_30px_rgba(26,22,47,0.06)]">
                <div className="space-y-8">
                    {/* Logo Upload */}
                    <div>
                        <label className="mb-3 block text-sm font-medium text-[#1A162F]">
                            {t('website.workspaceSettings.workspaceLogo')}
                        </label>
                        <div className="flex items-center gap-4">
                            <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-[10px] border border-[#E8EBF3] bg-[#F4F5F8]">
                                <WorkspaceAvatar
                                    logoUrl={logoUrl}
                                    name={workspaceName}
                                />
                            </div>
                            <button
                                onClick={() => logoInputRef.current?.click()}
                                disabled={isUploading || !canEdit}
                                className={clsx(
                                    'flex items-center gap-2 rounded-[10px] border px-4 py-2.5 text-sm font-medium transition-colors disabled:opacity-50',
                                    canEdit
                                        ? 'border-[#E8EBF3] bg-white text-[#1A162F] hover:bg-[#F8F9FC]'
                                        : 'cursor-not-allowed border-transparent bg-[#F8F9FC] text-[#8A93B2]'
                                )}
                            >
                                {isUploading ? (
                                    <>
                                        <CircleNotch size={16} weight="regular" className="animate-spin" />
                                        {t('website.workspaceSettings.uploading')}
                                    </>
                                ) : (
                                    <>
                                        <Upload size={16} weight="regular" />
                                        {t('website.workspaceSettings.uploadLogo')}
                                    </>
                                )}
                            </button>
                            <input
                                type="file"
                                ref={logoInputRef}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) handleLogoUpload(file);
                                }}
                            />
                        </div>
                    </div>

                    {/* Workspace Name */}
                    <div>
                        <label className="mb-2 block text-sm font-medium text-[#1A162F]">
                            {t('website.workspaceSettings.workspaceName')}
                        </label>
                        <input
                            type="text"
                            value={workspaceName}
                            onChange={(e) => setWorkspaceName(e.target.value)}
                            disabled={!canEdit}
                            className={clsx(
                                'w-full max-w-md rounded-[10px] border px-4 py-2.5 text-sm font-normal focus:outline-none transition-all',
                                canEdit
                                    ? 'border-[#E8EBF3] bg-white text-[#1A162F] placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20'
                                    : 'cursor-not-allowed border-transparent bg-[#F8F9FC] text-[#8A93B2]'
                            )}
                        />
                    </div>

                    {/* Industry Selection */}
                    <div>
                        <label className="mb-2 block text-sm font-medium text-[#1A162F]">
                            {t('website.workspaceSettings.industry')}
                        </label>
                        <DropdownMenu.Root
                            open={isIndustryDropdownOpen}
                            onOpenChange={(open) => canEdit && setIsIndustryDropdownOpen(open)}
                        >
                            <DropdownMenu.Trigger asChild>
                                <div
                                    className={clsx(
                                        'flex w-full max-w-md items-center justify-between rounded-[10px] border px-4 py-2.5 transition-all',
                                        canEdit
                                            ? 'cursor-pointer border-[#E8EBF3] bg-white hover:border-[#C8D0E2]'
                                            : 'cursor-not-allowed border-transparent bg-[#F8F9FC]',
                                        isIndustryDropdownOpen && canEdit && 'border-[#1A162F] ring-2 ring-[#1A162F]/20'
                                    )}
                                >
                                    {selectedIndustryData ? (
                                        <div className="flex items-center gap-2">
                                            <selectedIndustryData.icon size={20} weight="regular" className={canEdit ? 'text-[#1A162F]' : 'text-[#8A93B2]'} />
                                            <span className={clsx('font-normal', canEdit ? 'text-[#1A162F]' : 'text-[#8A93B2]')}>{selectedIndustryCopy?.label}</span>
                                        </div>
                                    ) : (
                                        <span className="text-[#8A93B2]">{t('website.workspaceSettings.selectIndustry')}</span>
                                    )}
                                    <CaretDown
                                        size={16}
                                        weight="regular"
                                        className={clsx(
                                            'transition-transform duration-200',
                                            canEdit ? 'text-[#8A93B2]' : 'text-[#A0A8C0]',
                                            isIndustryDropdownOpen && 'rotate-180'
                                        )}
                                    />
                                </div>
                            </DropdownMenu.Trigger>

                            <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                    className="z-[10002] origin-top overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                                    sideOffset={6}
                                    align="start"
                                    style={{ width: 'var(--radix-dropdown-menu-trigger-width)', maxWidth: 'calc(100vw - 16px)' }}
                                >
                                    <div className="max-h-[280px] overflow-y-auto space-y-0.5 p-1.5">
                                        {INDUSTRIES.map((ind) => {
                                            const isSelected = industry === ind.id;
                                            const Icon = ind.icon;
                                            const industryCopy = getIndustryCopy(ind);
                                            return (
                                                <DropdownMenu.Item
                                                    key={ind.id}
                                                    onSelect={() => {
                                                        setIndustry(ind.id);
                                                        setIsIndustryDropdownOpen(false);
                                                    }}
                                                    className={clsx(
                                                        'group flex cursor-pointer items-start gap-3 rounded-[10px] p-2.5 outline-none transition-colors',
                                                        isSelected ? 'bg-[#F4F5F8]' : 'hover:bg-[#F8F9FC]'
                                                    )}
                                                >
                                                    <div
                                                        className={clsx(
                                                            'shrink-0 rounded-[10px] p-2 transition-colors',
                                                            isSelected
                                                                ? 'bg-[#1A162F] text-white'
                                                                : 'bg-[#F4F5F8] text-[#666F8D] group-hover:bg-[#ECEFF5] group-hover:text-[#1A162F]'
                                                        )}
                                                    >
                                                        <Icon size={20} weight="regular" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center justify-between">
                                                            <span
                                                                className={clsx(
                                                                    'truncate pr-2 text-sm font-medium',
                                                                    isSelected ? 'text-[#1A162F]' : 'text-[#1A162F]'
                                                                )}
                                                            >
                                                                {industryCopy.label}
                                                            </span>
                                                            {isSelected ? (
                                                                <Check size={16} weight="regular" className="text-[#1A162F] shrink-0" />
                                                                    ) : null}
                                                        </div>
                                                        <p className="mt-0.5 line-clamp-1 text-xs text-[#666F8D]">
                                                            {industryCopy.description}
                                                        </p>
                                                    </div>
                                                </DropdownMenu.Item>
                                            );
                                        })}
                                    </div>
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>

                        {/* Other Industry Input */}
                        {industry === 'other' && (
                            <div className="mt-3">
                                <label className="mb-1 block text-sm font-medium text-[#666F8D]">
                                    {t('website.workspaceSettings.specifyIndustry')}
                                </label>
                                <input
                                    type="text"
                                    value={otherIndustry}
                                    onChange={(e) => setOtherIndustry(e.target.value)}
                                    disabled={!canEdit}
                                    placeholder={t('website.workspaceSettings.specifyIndustryPlaceholder')}
                                    className={clsx(
                                        'w-full max-w-md rounded-[10px] border px-4 py-2.5 text-sm font-normal focus:outline-none transition-all',
                                        canEdit
                                            ? 'border-[#E8EBF3] bg-white text-[#1A162F] placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:ring-2 focus:ring-[#1A162F]/20'
                                            : 'cursor-not-allowed border-transparent bg-[#F8F9FC] text-[#8A93B2]'
                                    )}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Save Button */}
                {hasChanges && canEdit ? (
                    <div className="mt-6 border-t border-[#E8EBF3] pt-6 animate-in fade-in slide-in-from-top-2 duration-300">
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="rounded-[10px] bg-[#1A162F] px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(26,22,47,0.12)] transition-colors hover:bg-[#231D3E] disabled:opacity-70"
                        >
                            {isSaving ? t('website.workspaceSettings.saving') : t('website.workspaceSettings.saveChanges')}
                        </button>
                    </div>
                ) : null}
            </div>

            {canTransferOwnership ? (
                <div className={clsx(
                    "rounded-[18px] border px-5 py-4",
                    pendingOwnerTransfer
                        ? "border-[#D9E1EE] bg-[#F7F9FC]"
                        : "border-[#E8EBF3] bg-[#FAFBFD]"
                )}>
                    <div className="flex items-center justify-between gap-4">
                        <div className="min-w-0 flex-1">
                            <h3 className="text-[15px] font-medium tracking-[-0.01em] text-[#1A162F]">
                                {pendingOwnerTransfer
                                    ? t('website.workspaceSettings.ownerTransferPendingTitle')
                                    : t('website.workspaceSettings.transferOwnership')}
                            </h3>
                            <p className="mt-1 text-sm font-normal leading-6 text-[#666F8D]">
                                {pendingOwnerTransfer
                                    ? t('website.workspaceSettings.ownerTransferPendingDescription', {
                                        name: pendingOwnerTransfer?.targetName || pendingOwnerTransfer?.targetEmail || 'the selected user',
                                    })
                                    : t('website.workspaceSettings.transferOwnershipDescription')}
                            </p>
                        </div>
                        {pendingOwnerTransfer ? (
                            <button
                                onClick={() => { void cancelOwnershipTransfer(); }}
                                disabled={isCancellingOwnerTransfer}
                                className="shrink-0 rounded-[10px] border border-[#D8DEEB] bg-white px-4 py-2.5 text-sm font-medium text-[#4A526A] transition-colors hover:bg-[#F7F9FC] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isCancellingOwnerTransfer
                                    ? t('website.workspaceSettings.cancellingOwnershipTransfer')
                                    : t('website.workspaceSettings.cancelOwnershipTransfer')}
                            </button>
                        ) : (
                            <button
                                onClick={() => setIsTransferModalOpen(true)}
                                disabled={!canStartOwnerTransfer}
                                className="shrink-0 rounded-[10px] border border-[#D8DEEB] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F7F9FC] disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {t('website.workspaceSettings.transfer')}
                            </button>
                        )}
                    </div>
                </div>
            ) : null}

            <Modal
                isOpen={isTransferModalOpen}
                onClose={() => {
                    if (isTransferring) return;
                    setIsTransferModalOpen(false);
                    setSelectedTransferUserId('');
                    setTransferTargetSnapshot(null);
                    setTransferSearchQuery('');
                }}
                title={t('website.workspaceSettings.transferOwnership')}
                width="max-w-xl"
                bodyClassName="px-6 pb-6 pt-4"
                showHeaderDivider
            >
                <div className="space-y-4">
                    <div>
                        <label className="mb-2 block text-sm font-medium text-[#1A162F]">
                            {t('website.workspaceSettings.selectNewOwner')}
                        </label>
                        <DropdownMenu.Root
                            open={isTransferDropdownOpen}
                            onOpenChange={(open) => {
                                setIsTransferDropdownOpen(open);
                                if (!open) setTransferSearchQuery('');
                            }}
                        >
                            <DropdownMenu.Trigger asChild>
                                <button
                                    type="button"
                                    className={clsx(
                                        'flex w-full items-center justify-between rounded-[10px] border px-3.5 py-2.5 text-left text-sm transition-all',
                                        isTransferDropdownOpen
                                            ? 'border-[#1A162F] bg-[#F8F9FC] ring-2 ring-[#1A162F]/20'
                                            : 'border-[#E8EBF3] bg-white hover:border-[#C8D0E2]'
                                    )}
                                >
                                    {selectedTransferTarget ? (
                                        <div className="flex min-w-0 items-center gap-2.5">
                                            <img
                                                src={getMemberAvatarUrl(selectedTransferTarget)}
                                                alt={selectedTransferTarget.displayName}
                                                className="h-6 w-6 shrink-0 rounded-full border border-gray-200 bg-white"
                                            />
                                            <div className="min-w-0">
                                                <span className="block truncate font-medium text-[#1A162F]">
                                                    {selectedTransferTarget.displayName}
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="font-normal text-[#8A93B2]">{t('website.workspaceSettings.selectUser')}</span>
                                    )}
                                    <CaretDown
                                        size={14}
                                        weight="regular"
                                        className={clsx(
                                            'shrink-0 text-[#8A93B2] transition-transform duration-200',
                                            isTransferDropdownOpen && 'rotate-180'
                                        )}
                                    />
                                </button>
                            </DropdownMenu.Trigger>

                            <DropdownMenu.Portal>
                                <DropdownMenu.Content
                                    className="z-[10002] overflow-hidden rounded-xl border border-[#E8EBF3] bg-white shadow-[0_16px_40px_rgba(26,22,47,0.12)] animate-in fade-in zoom-in-95 duration-100"
                                    sideOffset={6}
                                    align="start"
                                    collisionPadding={8}
                                    style={{ width: 'var(--radix-dropdown-menu-trigger-width)', maxWidth: 'calc(100vw - 16px)' }}
                                >
                                    <div className="border-b border-[#ECEFF5] p-2">
                                        <div className="relative">
                                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A93B2]" />
                                            <input
                                                type="text"
                                                value={transferSearchQuery}
                                                onChange={(e) => setTransferSearchQuery(e.target.value)}
                                                onKeyDown={(e) => e.stopPropagation()}
                                                placeholder={t('website.workspaceSettings.searchUsers')}
                                                className="w-full rounded-[10px] border border-[#E8EBF3] bg-[#FCFCFC] py-2 pl-9 pr-3 text-sm text-[#1A162F] transition-all placeholder:text-[#8A93B2] focus:border-[#1A162F] focus:outline-none focus:ring-1 focus:ring-[#1A162F]"
                                                autoFocus
                                            />
                                        </div>
                                    </div>

                                    <div className="max-h-[200px] overflow-y-auto p-1.5">
                                        {filteredTransferCandidates.length > 0 ? (
                                            <div className="space-y-0.5">
                                                {filteredTransferCandidates.map(member => {
                                                    const isSelected = selectedTransferUserId === member.userId;
                                                    return (
                                                        <DropdownMenu.Item
                                                            key={member.userId}
                                                            onSelect={() => {
                                                                setSelectedTransferUserId(member.userId);
                                                                setTransferTargetSnapshot(member);
                                                                setIsTransferDropdownOpen(false);
                                                                setTransferSearchQuery('');
                                                            }}
                                                            className={clsx(
                                                                'group flex cursor-pointer items-center gap-3 rounded-[10px] px-2.5 py-2.5 outline-none transition-colors',
                                                                isSelected ? 'bg-[#F4F5F8]' : 'hover:bg-[#F8F9FC]'
                                                            )}
                                                        >
                                                            <img
                                                                src={getMemberAvatarUrl(member)}
                                                                alt={member.displayName}
                                                                className="h-8 w-8 shrink-0 rounded-full border border-gray-200 bg-white"
                                                            />
                                                            <div className="min-w-0 flex-1">
                                                                <div className="flex items-center justify-between">
                                                                    <span className="truncate pr-2 text-sm font-medium text-[#1A162F]">
                                                                        {member.displayName}
                                                                    </span>
                                                                    <div className="flex shrink-0 items-center gap-2">
                                                                        <span className="rounded bg-[#F4F5F8] px-1.5 py-0.5 text-[11px] font-medium text-[#8A93B2]">
                                                                            {formatRoleLabel(member.role, t, { titleCaseFallback: true })}
                                                                        </span>
                                                                        {isSelected ? (
                                                                            <Check size={14} weight="regular" className="text-[#1A162F]" />
                                                                        ) : null}
                                                                    </div>
                                                                </div>
                                                                <p className="mt-0.5 truncate text-xs text-[#666F8D]">
                                                                    {member.email}
                                                                </p>
                                                            </div>
                                                        </DropdownMenu.Item>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div className="px-3 py-6 text-center text-sm text-[#8A93B2]">
                                                {t('website.workspaceSettings.noUsersFound')}
                                            </div>
                                        )}
                                    </div>
                                </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                        </DropdownMenu.Root>
                    </div>

                    <div className="rounded-xl border border-[#E8EBF3] bg-[#F8F9FC] px-4 py-3">
                        <p className="text-sm leading-relaxed text-[#666F8D]">
                            {t('website.workspaceSettings.ownershipTransferPreviewPrefix')}
                            <span className="font-medium text-[#1A162F]">
                                {selectedTransferTarget?.displayName || t('website.workspaceSettings.selectedTransferUserFallback')}
                            </span>
                            {t('website.workspaceSettings.ownershipTransferPreviewMiddle')}
                            <span className="font-medium text-[#1A162F]">{t('website.membersSettings.adminRole')}</span>
                            {t('website.workspaceSettings.ownershipTransferPreviewSuffix')}
                        </p>
                    </div>

                    <div className="flex justify-end gap-3 pt-1">
                        <button
                            onClick={() => {
                                setIsTransferModalOpen(false);
                                setSelectedTransferUserId('');
                                setTransferTargetSnapshot(null);
                            }}
                            disabled={isTransferring}
                            className="rounded-[10px] border border-[#E8EBF3] bg-white px-4 py-2.5 text-sm font-medium text-[#1A162F] transition-colors hover:bg-[#F8F9FC] disabled:opacity-50"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={() => { void handleTransferOwnership(); }}
                            disabled={!selectedTransferTarget || isTransferring || !canStartOwnerTransfer}
                            className="flex items-center justify-center gap-2 rounded-[10px] bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-[0_10px_20px_rgba(220,38,38,0.12)] transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {isTransferring ? (
                                <>
                                    <CircleNotch className="h-4 w-4 animate-spin" />
                                    <span>{t('website.membersSettings.transferring')}</span>
                                </>
                            ) : (
                                t('website.membersSettings.transferOwnership')
                            )}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default WorkspaceSettings;
